import { createContext, useCallback, useContext, useRef, useState } from 'react';

// Noise Suppression:
// - ПОЛНОСТЬЮ ОТКЛЮЧЕН (всегда false)
// - Функциональность деактивирована

const VoiceAssistantContext = createContext(null);

export const VoiceAssistantProvider = ({ children }) => {
  const openVoiceAssistantRef = useRef(null);
  // Issue #944: VoiceWidget registers its openChat() implementation here. The
  // ref + handler pattern (mirrors openVoiceAssistantRef above) lets any caller
  // — including SDK CustomEvent, Sparkles buttons, third-party host pages —
  // converge on a single text-init flow inside the widget (load history, bump
  // textChatVersion, connect WS, send greeting). NO LiveKit for text mode.
  const openChatHandlerRef = useRef(null);
  // Как закрыть виджет на самом деле (#1441): без него `closeVoiceAssistant`
  // только опускал флаг `isOpen`, а голосовая сессия виджета продолжалась —
  // карточка звонка «клала трубку», а ассистент слушал дальше.
  const closeHandlerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false); // Виджет закрыт по умолчанию
  // Идёт ли изолированный звонок (#1441). Публикует виджет: звонок может
  // кончиться не по кнопке карточки — трубку положил агент, соединение не
  // поднялось, — и карточка должна это увидеть.
  const [isolatedCallActive, setIsolatedCallActive] = useState(false);
  const [initialMode, setInitialMode] = useState(null); // 'voice' | 'text' | null
  // launchContext - контекст запуска (какая кнопка/действие вызвало открытие)
  // { action: 'search_with_ai' | 'book_test_drive' | 'ask_question' | 'direct_open', metadata?: {...} }
  const [launchContext, setLaunchContext] = useState(null);
  // Noise suppression ОТКЛЮЧЕН - всегда false
  const isNoiseSuppressionEnabled = false;

  const setOpenHandler = (handler) => {
    openVoiceAssistantRef.current = handler;
  };

  // Issue #944: VoiceWidget calls this on mount to register its openChat()
  // implementation — the single source of truth for the chat-open flow.
  const setOpenChatHandler = useCallback((handler) => {
    openChatHandlerRef.current = handler;
  }, []);

  /**
   * Открыть голосовой ассистант
   */
  const openVoiceAssistant = () => {
    setIsOpen(true); // Открыть виджет
    // Вызвать handler для старта conversation (если установлен)
    if (openVoiceAssistantRef.current) {
      openVoiceAssistantRef.current();
    }
  };

  /**
   * Закрыть голосовой ассистант
   */
  const closeVoiceAssistant = () => {
    setIsOpen(false); // Закрыть виджет
    closeHandlerRef.current?.();
  };

  const setCloseHandler = useCallback((handler) => {
    closeHandlerRef.current = handler;
  }, []);

  /**
   * Issue #944: Unified entry point for opening the chat widget with an optional
   * launch context. Routes through the VoiceWidget's registered openChat handler
   * (single source of truth — same flow as a manual chat-bubble click). For
   * ``mode === 'voice'`` it falls back to the legacy LiveKit-connect path.
   *
   * Issue #944: this is the only public chat-open entry point — the legacy
   * ``openVoiceAssistantInMode`` shim was removed in Phase 4 (!951).
   *
   * @param {Object} opts
   * @param {'text' | 'chat' | 'voice'} [opts.mode='chat'] — 'text' is an alias for 'chat'.
   * @param {string} [opts.action] — typed action name, e.g. 'ask_about_car', 'search_with_ai'.
   * @param {Object} [opts.metadata] — action-specific metadata (carTitle, searchQuery, …).
   * @param {string} [opts.targetPage] — for the internal 'page_navigated' action.
   * @param {boolean} [opts.isolated] — voice only: a separate agent session that
   *   leaves the chat history untouched (the showcase phone call, #1441).
   */
  const openChat = useCallback((opts = {}) => {
    const { mode = 'chat', action, metadata, targetPage, isolated } = opts;
    const normalizedMode = mode === 'text' ? 'chat' : mode;

    // The widget's handler runs the full init (history, useTextChat reinit,
    // WS, greeting). Always prefer it.
    if (openChatHandlerRef.current) {
      return openChatHandlerRef.current({ mode: normalizedMode, action, metadata, targetPage, isolated });
    }

    // Pre-mount fallback: VoiceWidget hasn't registered yet. Set React state so
    // the widget's initialMode useEffect picks up the change once it mounts.
    console.warn(
      '[VoiceAssistantContext] openChat called before widget handler registered — falling back to React state path'
    );
    setInitialMode(normalizedMode === 'voice' ? 'voice' : 'text');
    if (action || metadata || targetPage) {
      setLaunchContext({ action, metadata, targetPage });
    }
    setIsOpen(true);
    return undefined;
  }, []);

  /**
   * Сбросить initialMode (вызывается assistants после использования)
   */
  const clearInitialMode = () => {
    setInitialMode(null);
  };

  /**
   * Сбросить launchContext (вызывается после отправки контекста агенту)
   */
  const clearLaunchContext = () => {
    setLaunchContext(null);
  };

  /**
   * #1081: Set the full launchContext object BEFORE calling openChat({mode:'chat'})
   * so the widget's openChat handler forwards it VERBATIM to the agent (its
   * fallback path sends the React-Context launchContext as-is). Needed for the
   * form→chat handover, whose frozen launchContext carries top-level fields
   * (`leadAlreadyCaptured`) that the `{action, metadata}` shorthand would drop.
   */
  const setFullLaunchContext = useCallback((ctx) => {
    setLaunchContext(ctx || null);
  }, []);

  /**
   * Переключить состояние noise suppression
   * @returns {boolean} ВСЕГДА false (функциональность отключена)
   */
  const toggleNoiseSuppression = () => {
    console.log('[VoiceAssistantContext] Noise suppression is DISABLED (toggle ignored)');
    return false; // Всегда false
  };

  /**
   * Включить noise suppression
   * ФУНКЦИОНАЛЬНОСТЬ ОТКЛЮЧЕНА - метод ничего не делает
   */
  const enableNoiseSuppression = () => {
    console.log('[VoiceAssistantContext] Noise suppression is DISABLED (enable ignored)');
    // Ничего не делаем - функциональность отключена
  };

  /**
   * Выключить noise suppression
   * ФУНКЦИОНАЛЬНОСТЬ ОТКЛЮЧЕНА - метод ничего не делает
   */
  const disableNoiseSuppression = () => {
    console.log('[VoiceAssistantContext] Noise suppression is DISABLED (disable ignored)');
    // Ничего не делаем - функциональность отключена
  };

  return (
    <VoiceAssistantContext.Provider
      value={{
        setOpenHandler,
        openVoiceAssistant,
        closeVoiceAssistant,
        setCloseHandler,
        isolatedCallActive,
        setIsolatedCallActive,
        clearInitialMode,
        isOpen,
        initialMode,
        // Launch Context - контекст запуска виджета
        launchContext,
        clearLaunchContext,
        setFullLaunchContext,
        // Issue #944: unified chat-open entry point.
        openChat,
        setOpenChatHandler,
        // Noise Suppression
        isNoiseSuppressionEnabled,
        toggleNoiseSuppression,
        enableNoiseSuppression,
        disableNoiseSuppression,
      }}
    >
      {children}
    </VoiceAssistantContext.Provider>
  );
};

export const useVoiceAssistant = () => {
  const context = useContext(VoiceAssistantContext);
  if (!context) {
    throw new Error('useVoiceAssistant must be used within VoiceAssistantProvider');
  }
  return context;
};
